import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Row, Col } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import * as fromActions from 'store/actions/participants.action'
import { IMainState } from 'store/states'
import { IAreaModel } from 'models/area.model'
import { ListOriginEnum } from 'enum/listOrigin.enum'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import * as fromAreaActions from 'store/actions/areas.action'
import * as fromProfilesActions from 'store/actions/lms-profiles.action'
import participantIdsList from 'api/administration/participants/GetAllParticipants'
import { canSkipOnRowSelected } from 'helpers'
import TableUsers from 'components/ui/TableUsers'
import { useColumns } from './hooks'
import AddParticipantsModal from './components/add-participants-modal'
import AddProfilesModal from './components/add-profiles-modal'
import UploadButton from './components/upload-button/UploadButton'
import styles from './Users.module.scss'
import Wrapper from 'components/reusables/wrapper'

const MIN_SEARCH_LENGTH = 3

const { closed, removeProfiles, disableUsers } = {
  closed: 0,
  removeProfiles: 1,
  disableUsers: 2,
}

// Esta vista puede crecer más ? Si, si puede
const Users: React.FC = () => {
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showAddProfilesModal, setShowAddProfilesModal] = useState(false)
  const [showAddParticipantsModal, setShowAddParticipantsModal] =
    useState(false)
  const [confirmationModal, setConfirmationModal] = useState<{
    type: number
    onClick?: () => void
  }>({
    type: closed,
  })
  const [selectedArea, setSelectedArea] = useState({} as any)
  const [page, setPage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [sort, setSort] = useState({})
  const [perPage, setPerPage] = useState(10)
  const [selectedRows, setSelectedRows] = useState([] as any)
  const [loading, setLoading] = useState(true)
  const [selectedFilters, setSelectedFilters] = useState({} as any)
  const [fastFilters, setFastFilters] = useState(0 as any)
  const {
    list,
    total,
    loading: participantsLoading,
  } = useSelector(({ participants }: IMainState) => participants)
  const { areas, loading: areasLoading } = useSelector(
    ({ areas }: IMainState) => areas
  )

  const areaRef = useRef([] as any)
  const searchTextRef = useRef([] as any)
  const filtersRef = useRef([] as any)

  const dispatch = useDispatch()
  const { t } = useTranslation()
  let bounceId: any

  useEffect(() => {
    dispatch({ type: fromAreaActions.GET_AREAS_LIST })
    dispatch({ type: fromProfilesActions.LMS_ALL_PROFILES_LIST })
  }, [dispatch])

  useEffect(() => {
    setLoading(participantsLoading || areasLoading)
  }, [participantsLoading, areasLoading])

  useEffect(() => {
    setLoading(true)
    areaRef.current = selectedArea.successorHierarchy
    searchTextRef.current = searchText
    filtersRef.current = selectedFilters

    const timer = setTimeout(() => {
      dispatch({
        type: fromActions.PARTICIPANTS_PROFILES_LIST,
        payload: {
          page,
          area: selectedArea.successorHierarchy,
          searchText,
          sort,
          usersFilter: selectedFilters,
          fastFilters,
          perPage,
        },
      })
    }, 1000)
    return () => clearTimeout(timer)
  }, [
    page,
    sort,
    searchText,
    perPage,
    selectedArea,
    selectedFilters,
    fastFilters,
    dispatch,
  ])

  const dataSource = useMemo(
    () =>
      list.map(
        ({
          fullName,
          avatar,
          identifier,
          area,
          position,
          roleId,
          lmsProfiles,
          id,
          supervisors,
        }) => ({
          key: id,
          full_name: { fullName, position, avatar, roleId, id },
          identifier,
          area_name: area?.name || '',
          supervisor_name: supervisors,
          lms_profiles_id: lmsProfiles,
        })
      ),
    [list]
  )

  useEffect(() => {
    setSelectedRows([] as number[])
  }, [selectedFilters])

  const columns = useColumns()

  const actions = [
    {
      label: t('configuration.users.bulkLoad'),
      group: [
        {
          label: t('configuration.users.upload.titleNew'),
          onClick: () => setShowUploadModal(true),
        },
        {
          label: t('configuration.users.download.title'),
          onClick: () => handleDownload(),
        },
        {
          label: t('configuration.users.downloadDisabled.title'),
          onClick: () => handleDownloadDisabled(),
        },
      ],
    },
    {
      label: t('configuration.users.usersManagement.title'),
      disabled: !selectedRows.length,
      group: [
        {
          label: t('configuration.users.usersManagement.addProfiles'),
          onClick: () => setShowAddProfilesModal(true),
        },
        {
          label: t('configuration.users.usersManagement.removeAllProfiles'),
          onClick: () => {
            setConfirmationModal({
              type: removeProfiles,
              onClick: () => handleRemoveAllProfiles(),
            })
          },
        },
        {
          label: t('configuration.users.usersManagement.disableUsers'),
          onClick: () => {
            setConfirmationModal({
              type: disableUsers,
              onClick: () => handleDisableUser(),
            })
          },
        },
      ],
    },
  ]

  const handleDownload = () => {
    dispatch({ type: fromActions.PARTICIPANTS_DOWNLOAD })
  }

  const handleDownloadDisabled = () => {
    dispatch({
      type: fromActions.PARTICIPANTS_DOWNLOAD,
      payload: { disabled: true },
    })
  }

  const handleSearch = (value: any) => {
    bounceId && clearTimeout(bounceId)
    if (value.length >= MIN_SEARCH_LENGTH || !value.length)
      bounceId = setTimeout(() => {
        setSearchText(value.trim())
        setPage(1)
      }, 1000)
  }

  const handleRemoveAllProfiles = () => {
    dispatch({
      type: fromActions.REMOVE_PROFILES_BULK_ACTION,
      payload: {
        ids: selectedRows,
        listConfig: {
          page,
          area: selectedArea.successorHierarchy,
          searchText,
          sort,
          usersFilter: selectedFilters,
          fastFilters,
          perPage,
        },
      },
    })
    handleCloseConfirmationModal()
  }

  const handleDisableUser = () => {
    setSelectedRows([])
    dispatch({
      type: fromActions.PARTICIPANTS_SET_ACTIVE,
      payload: {
        origin: ListOriginEnum.Users,
        ids: selectedRows,
        listConfig: {
          page,
          area: selectedArea.successorHierarchy,
          searchText,
          sort,
          usersFilter: selectedFilters,
          fastFilters,
          perPage,
        },
        state: false,
      },
    })
    handleCloseConfirmationModal()
  }

  const handleCloseConfirmationModal = () => {
    setConfirmationModal({ type: closed })
  }

  const handleSetSelectedArea = (selectedArea: any) => {
    setSelectedArea(selectedArea)
    setPage(1)
  }

  const handleAllSelected = async () => {
    const { data } = await participantIdsList(
      areaRef.current,
      searchTextRef.current,
      filtersRef.current
    )
    setSelectedRows(data.map(({ id }: any) => id))
  }

  const handleFiltersChange = (filtersData: any) => {
    setSelectedFilters(filtersData)
    setPage(1)
  }

  const handleResetTable = () => {
    setPage(1)
    setSearchText('')
    setSelectedFilters({})
    setSelectedArea({})
  }

  const handleSetFastAction = (value: any) => {
    setFastFilters(value)
    setPage(1)
  }

  return (
    <Wrapper shadow={true}>
      <Row>
        <Col xs={12}>
          <h1 className={styles.title}>{t('configuration.users.title')}</h1>
          <p className={styles.legend}>{t('configuration.users.legend')}</p>
        </Col>
      </Row>
      <Row>
        <Col>
          <TableUsers
            columnHeaders={columns}
            data={dataSource}
            total={total}
            areas={areas}
            mandatoryAreas={false}
            actions={actions}
            currentPage={page}
            loading={loading}
            search={searchText}
            selectedRows={selectedRows}
            selectedArea={selectedArea}
            selectedFilters={selectedFilters}
            onFilterChange={handleFiltersChange}
            onAreaSelected={(data: IAreaModel) => handleSetSelectedArea(data)}
            onAddClick={() => setShowAddParticipantsModal(true)}
            onSearch={handleSearch}
            onSortChange={(sortData: any) => setSort(sortData)}
            onRowSelected={({ selectedRowKeys, dispatcher }: any) => {
              if (canSkipOnRowSelected(dispatcher)) return
              return setSelectedRows(selectedRowKeys)
            }}
            onPaginationChange={(currentPage: number) => setPage(currentPage)}
            onRowsPerPageChange={(itemsPerPage: number) =>
              setPerPage(itemsPerPage)
            }
            onAllRowKeys={handleAllSelected}
            onFastActionSelect={handleSetFastAction}
          />
        </Col>
      </Row>
      {showAddProfilesModal && (
        <AddProfilesModal
          show={showAddProfilesModal}
          onClose={() => setShowAddProfilesModal(false)}
          ids={selectedRows}
          listConfig={{
            page,
            area: selectedArea.successorHierarchy,
            searchText,
            sort,
            usersFilter: selectedFilters,
            fastFilters,
            perPage,
          }}
        />
      )}
      {showUploadModal && (
        <UploadButton show={showUploadModal} setShow={setShowUploadModal} />
      )}
      {showAddParticipantsModal && (
        <AddParticipantsModal
          resetFilters={handleResetTable}
          show={showAddParticipantsModal}
          onClose={() => setShowAddParticipantsModal(false)}
          originalListConfig={{
            page: 1,
            area: [],
            searchText: '',
            sort,
            usersFilter: {},
            perPage,
          }}
        />
      )}
      {!!confirmationModal.type && (
        <ConfirmationModal
          show={!!confirmationModal.type}
          title=""
          text={t(
            `configuration.removeModal.${
              confirmationModal.type === 1
                ? 'removeProfiles'
                : 'disableSelectedUsers'
            }`
          )}
          acceptButtonText={t(
            `configuration.removeModal.buttons.${
              confirmationModal.type === 1 ? 'removeProfiles' : 'disableUsers'
            }`
          )}
          onAccept={() => confirmationModal.onClick?.()}
          onClose={() => handleCloseConfirmationModal()}
        />
      )}
    </Wrapper>
  )
}

export default Users
